import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Layout } from 'lucide-react';
import {
  STEP_WRAPPER_CLASS,
  STEP_MOTION_CLASS,
  STEP_CARD_COMPACT_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_NAV_CLASS,
  STEP_INNER_HEADER_CLASS,
  STEP_PRIMARY_CTA_CLASS,
} from '../shared/stepLayout';
import { TEMPLATE_LIBRARY, TemplateThumbnail } from '../ResultsStep';

/** Legacy alias — kept for any code that still imports TEMPLATE_OPTIONS */
export const TEMPLATE_OPTIONS = TEMPLATE_LIBRARY.map(t => ({
  id: t.id,
  labelKey: `optimizer.template_${t.id.replace(/-/g, '_')}`,
  label: t.label,
}));

export type TemplateId = string;

interface TemplateStepProps {
  selectedTemplateId: string;
  onNext: (templateId: string) => void;
  onBack: () => void;
}

/**
 * Choose template step — shows all 9 supported skins as realistic thumbnails.
 */
const TemplateStep: React.FC<TemplateStepProps> = ({
  selectedTemplateId,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string>(selectedTemplateId || 'ats-optimized');

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={STEP_MOTION_CLASS}
    >
      <div className={STEP_WRAPPER_CLASS}>
        <div className={STEP_SCROLL_AREA_CLASS}>
          <div className={STEP_CARD_COMPACT_CLASS}>
            <div className={STEP_INNER_HEADER_CLASS}>
              <div className="flex items-center gap-2">
                <Layout className="h-5 w-5 text-[#6C47FF] dark:text-[#5B8DFF]" />
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  [ {t('optimizer.chooseTemplate', 'Choose a template')} ]
                </h2>
              </div>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                {t('optimizer.chooseTemplateDesc', 'All 9 designs are ATS-friendly. Your optimized resume will be rendered exactly as shown below.')}
              </p>
            </div>

            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {TEMPLATE_LIBRARY.map((tmpl) => (
                  <div key={tmpl.id}>
                    <TemplateThumbnail
                      tpl={tmpl}
                      selected={selected === tmpl.id}
                      onClick={() => setSelected(tmpl.id)}
                    />
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1 text-center truncate px-1">
                      {tmpl.roles.slice(0, 2).join(' · ')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <nav className={STEP_NAV_CLASS} aria-label="Step navigation">
          <button
            type="button"
            onClick={onBack}
            aria-label={t('optimizer.backToResume', 'Back to Resume')}
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 sm:px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium rounded-lg transition-colors touch-manipulation flex-initial min-w-0"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">[ {t('optimizer.backToResume', 'Back to Resume')} ]</span>
          </button>
          <button
            type="button"
            onClick={() => onNext(selected)}
            aria-label={t('optimizer.continueToReview', 'Continue to Review')}
            className={`flex min-h-[44px] flex-initial min-w-0 touch-manipulation items-center justify-center gap-2 px-4 py-3 sm:px-6 ${STEP_PRIMARY_CTA_CLASS}`}
          >
            <span className="hidden sm:inline">[ {t('optimizer.continueToReview', 'Continue to Review')} ]</span>
            <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
        </nav>
      </div>
    </motion.div>
  );
};

export default TemplateStep;
